import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getReleases, getDSPServices, getSocialPlatforms, seedDefaults } from "@/lib/api";
import ReleaseCard from "@/components/ReleaseCard";
import MainNav from "@/components/MainNav";
import { Skeleton } from "@/components/ui/skeleton";

export default function PublicWidget() {
  const [releases, setReleases] = useState([]);
  const [dspServices, setDspServices] = useState([]);
  const [socialPlatforms, setSocialPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed defaults first
        await seedDefaults();
        
        const [releasesRes, dspRes, socialRes] = await Promise.all([
          getReleases(),
          getDSPServices(),
          getSocialPlatforms(),
        ]);
        setReleases(releasesRes.data);
        setDspServices(dspRes.data);
        setSocialPlatforms(socialRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <MainNav />

      {/* Releases Grid */}
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-syne font-bold text-white" data-testid="recordings-title">Recordings</h1>
        </motion.div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="releases-loading">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-xl overflow-hidden bg-background-surface">
                <Skeleton className="aspect-square w-full" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : releases.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
            data-testid="no-releases"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-zinc-900 flex items-center justify-center">
              <svg className="w-12 h-12 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <h3 className="text-xl font-syne font-bold text-zinc-300 mb-2">No Releases Yet</h3>
            <p className="text-zinc-500">Check back soon for new music!</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="releases-grid">
            {releases.map((release, index) => (
              <motion.div
                key={release.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <ReleaseCard
                  release={release}
                  dspServices={dspServices}
                  socialPlatforms={socialPlatforms}
                />
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
